import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  TableCell,
  Badge,
  Tooltip,
  Chip,
  alpha,
  Link,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Archive, Edit } from "lucide-react";
import DataTable from "bundles/Internal/components/common/DataTable";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import JobApplicationTableFilters from "bundles/Internal/components/job_application/JobApplicationTableFilters";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "candidate_name", label: "Candidate" },
  { key: "candidate_email", label: "Email" },
  { key: "candidate_phone", label: "Phone" },
  { key: "job_title", label: "Job Title" },
  { key: "company_name", label: "Company" },
  { key: "status", label: "Status" },
  { key: "resume_url", label: "Resume" },
  { key: "created_at", label: "Applied At" },
  { key: "created_by", label: "Created by" },
  { key: "sent_from_talent_team", label: "Talent Team" },
  { key: "added_from_cvtheque", label: "CV Library" },
  { key: "utm_source", label: "UTM Source" },
];

export const STATUS_COLOR = {
  pending: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  open: { border: "#2196F3", bg: "rgba(33,150,243,0.10)", text: "#2196F3" },
  saved: { border: "#9C27B0", bg: "rgba(156,39,176,0.10)", text: "#9C27B0" },
  accepted: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  closed: { border: "#757575", bg: "rgba(117,117,117,0.10)", text: "#757575" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const DATE_KEYS = new Set(["created_at"]);

function formatJobApplicationCell(jobApplication, key) {
  const value = jobApplication?.[key];

  if (DATE_KEYS.has(key)) return formatDateTime(value);

  if (key === "created_by") {
    const c = jobApplication?.created_by;
    if (!c) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UserAvatar user={c} size={22} />
        <Box component="span">{c.full_name || c.email || "Unknown"}</Box>
      </Box>
    );
  }

  if (key === "status" && value) {
    return pill(value, STATUS_COLOR[value] || STATUS_COLOR.pending);
  }

  if (key === "resume_url" && value) {
    return (
      <Link
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "#079344",
          fontWeight: "Segoe UI Semibold, sans-serif, system-ui",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Resume
      </Link>
    );
  }

  if (key === "sent_from_talent_team" || key === "added_from_cvtheque") {
    return value ? (
      <CheckCircleIcon sx={{ fontSize: 18, color: "#079344" }} />
    ) : null;
  }

  return formatValue(value);
}

const JobApplicationTable = ({
  jobApplications = [],
  onJobApplicationSelect,
  onOpenNotes,
  onEdit,
  onDelete,
  onQueryChange,
  filters,
  onFiltersChange,
  creators = [],
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onExportCsv,
  onAddClick,
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuJobApplication, setMenuJobApplication] = useState(null);

  const renderCell = useCallback((jobApplication, key) => {
    const value = jobApplication?.[key];

    if (key === "candidate_name" && value) {
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            if (jobApplication.candidate_id) navigate(`/internal/candidates/${jobApplication.candidate_id}`);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }

    if (key === "job_title" && value) {
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            if (jobApplication.job_offer_id) navigate(`/internal/job_offers/${jobApplication.job_offer_id}`);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }

    if (key === "company_name" && value) {
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            if (jobApplication.company_id) navigate(`/internal/companies/${jobApplication.company_id}`);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }

    if (key === "candidate_email" && value) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</Box>
          <CopyButton value={value} />
        </Stack>
      );
    }

    if (key === "candidate_phone" && value) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</Box>
          <CopyButton value={value} />
        </Stack>
      );
    }

    return formatJobApplicationCell(jobApplication, key);
  }, [navigate]);

  return (
    <>
      <DataTable
        title="Job Applications"
        totalLabel={(count) => `${count.toLocaleString()} total job applications`}
        emptyIcon={AssignmentIcon}
        emptyTitle="No job applications found"
        emptySubtitle="Try adjusting your search criteria"
        columns={columns}
        data={jobApplications}
        renderCell={renderCell}
        extraHeadColumns={["Notes"]}
        renderExtraCells={(jobApplication) => {
          const notesCount = Number(jobApplication?.notes_count || 0);
          return (
            <TableCell key="notes" sx={{ whiteSpace: "nowrap", py: 1.5 }}>
              <Tooltip title="View notes" arrow placement="top">
                <IconButton
                  size="small"
                  aria-label="Open notes"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenNotes?.(jobApplication);
                  }}
                  disabled={!onOpenNotes}
                  sx={{
                    backgroundColor:
                    notesCount > 0
                      ? alpha("#079344", 0.1)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: alpha("#079344", 0.2),
                    },
                  }}
                >
                  <Badge
                    badgeContent={notesCount}
                    color="primary"
                    invisible={notesCount === 0}
                    overlap="circular"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#079344",
                        fontWeight: 600,
                        fontSize: 10,
                      },
                    }}
                  >
                    <NoteAddOutlinedIcon
                      fontSize="small"
                      sx={{
                        color:
                        notesCount > 0 ? "#079344" : "text.secondary",
                      }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            </TableCell>
          );
        }}
        onRowClick={onJobApplicationSelect}
        onAddClick={onAddClick}
        addButtonLabel="New Application"
        onQueryChange={onQueryChange}
        searchPlaceholder="Search by candidate name, email, phone, job title, company…"
        searchInfo="Candidate name, email, phone, job title, company"
        searchById
        filtersComponent={
          filters && onFiltersChange ? (
            <JobApplicationTableFilters filters={filters} onFiltersChange={onFiltersChange} creators={creators} />
          ) : null
        }
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        loading={loading}
        selectable={selectable}
        selectedRows={selectedRows}
        onRowSelect={onRowSelect}
        onExportCsv={onExportCsv}
        tailHeadColumns={[""]}
        renderTailCells={(jobApplication) => (
          <TableCell key="actions" sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
                setMenuJobApplication(jobApplication);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}
      />
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuJobApplication(null); }}
        onClick={() => { setMenuAnchor(null); }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => { onEdit?.(menuJobApplication); setMenuJobApplication(null); }}>
          <ListItemIcon><Edit size={16} /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onDelete?.(menuJobApplication); setMenuJobApplication(null); }}>
          <ListItemIcon><Archive size={16} color="#d32f2f" /></ListItemIcon>
          <ListItemText sx={{ color: "#d32f2f" }}>Archive</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default JobApplicationTable;
